import { Box, Button, Toolbar } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { getCompletedOrders, IDriverOrder } from "@/api/profile";
import { formatDate } from "@/utils/formatDate";
import {
    DataTable,
    DriverModal,
    EnhancedTableToolBar,
} from "@/components/dashboard";
import {
    OrderStatus,
    OrderStatusChip,
} from "@/components/dashboard/orders/status/OrderStatusChip";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useFilterModel } from "@/hooks";
import { buildQuery } from "@/api/api";
import React from "react";

interface IPastOrderRow {
    id: number;
    store: string;
    created_at: Date;
    delivered_time: string;
    delivery_window: string;
    order_status: Omit<OrderStatus, "paid" | "unpaid">;
    order: IDriverOrder;
}

export const PastOrders = () => {
    const [openModal, setOpenModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<IDriverOrder | null>(
        null
    );
    const [showOrderLocation, setShowOrderLocation] = useState(false);

    const { filterModel, handleFilterModelChange } = useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const {
        data: completedOrdersData,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: [
            "completed-orders",
            paginationModel.page,
            paginationModel.pageSize,
            buildQuery(filterModel),
        ],
        queryFn: () =>
            getCompletedOrders(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    const autosizeOptions = {
        columns: [
            "store",
            "created_at",
            "delivered_time",
            "delivery_window",
            "order_status",
            "actions",
        ],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const columns: GridColDef<IPastOrderRow>[] = useMemo(
        () => [
            {
                field: "id",
                headerName: "Order",
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => {
                    if (!value) return value;

                    return `#${value}`;
                },
                display: "flex",
            },
            {
                field: "store",
                headerName: "Store Name",
                display: "flex",
            },
            {
                field: "created_at",
                headerName: "Order Date",
                display: "flex",
                valueFormatter: (value) => {
                    if (!value) return value;

                    return formatDate(value);
                },
            },
            {
                field: "delivered_time",
                headerName: "Delivered",
                display: "flex",
            },
            {
                field: "delivery_window",
                headerName: "Delivery Window",
                display: "flex",
                type: "singleSelect",
                valueOptions: [
                    { value: "7 Days", label: "7 Days" },
                    { value: "5 Days", label: "5 Days" },
                    { value: "3 Days", label: "3 Days" },
                    { value: "2 Days", label: "2 Days" },
                ],
            },
            {
                field: "order_status",
                type: "singleSelect",
                headerName: "Order Status",
                display: "flex",
                valueOptions: [{ value: "delivered", label: "Delivered" }],
                renderCell({ row }) {
                    return (
                        <OrderStatusChip
                            orderStatus="delivered"
                            order_id={row.id}
                            allowStatusUpdate={false}
                        />
                    );
                },
            },
            {
                field: "actions",
                type: "actions",
                renderCell({ row }) {
                    return (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleOpen(row.order)}
                        >
                            View details
                        </Button>
                    );
                },
            },
        ],

        []
    );

    const handleClose = () => {
        setOpenModal(false);
    };

    const handleChange = (
        _event: React.MouseEvent<HTMLElement>,
        toggleValue: boolean
    ) => {
        setShowOrderLocation(toggleValue);
    };

    const handleOpen = (order: IDriverOrder) => {
        setSelectedOrder(order);
        setOpenModal(true);
    };

    const pastOrderRows: IPastOrderRow[] = useMemo(() => {
        if (!completedOrdersData?.items) return [];
        return completedOrdersData.items.map((order) => {
            const created_at_date = new Date(order.created_at);
            const delivered_time = order.delivered_time
                ? new Date(order.delivered_time).toLocaleDateString()
                : "";
            return {
                id: order.id,
                store: order.store?.name ?? "Custom Order",
                created_at: created_at_date,
                delivered_time,
                delivery_window: order.delivery_window,
                order_status: "delivered",
                order,
            } as IPastOrderRow;
        });
    }, [completedOrdersData]);

    const rowCountRef = useRef(completedOrdersData?.total || 0);

    const rowCount = useMemo(() => {
        if (completedOrdersData?.total !== undefined) {
            rowCountRef.current = completedOrdersData.total;
        }
        return rowCountRef.current;
    }, [completedOrdersData?.total]);

    return (
        <Box
            sx={{
                backgroundColor: "#F4F7FE",
                minHeight: "100vh",
                width: "100%",
                overflowX: "auto",
            }}
        >
            <Toolbar sx={{ display: { sm: "none" } }} />

            <DriverModal
                open={openModal}
                onClose={handleClose}
                order={selectedOrder}
                showOrderLocation={showOrderLocation}
                onToggle={handleChange}
                showAcceptButton={false}
                refreshOrder={async () => {
                    await refetch();
                }}
            />

            <Box sx={{ borderRadius: "10px", padding: "1em" }}>
                <EnhancedTableToolBar title={"Past Orders"} />
                <DataTable
                    rows={pastOrderRows}
                    loading={isLoading}
                    columns={columns}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: "id", sort: "desc" }],
                        },
                    }}
                    autoSize
                    autosizeOptions={autosizeOptions}
                    paginationMode={"server"}
                    rowCount={rowCount}
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    filterMode={"server"}
                    sortingMode={"server"}
                    onFilterModelChange={handleFilterModelChange}
                    onSortModelChange={handleFilterModelChange}
                />
            </Box>
        </Box>
    );
};
